import * as React from 'react';

import moment from 'moment';

import {isToday, isYesterday} from 'nutshell-core/date-time';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailSeriesIcon} from 'shells/icon/svg/email-series-icon';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {Table} from 'shells/table/table';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {Template} from './types';

import styles from './group-table.module.css';

type Props = {
    templates: Template[];
    errorMessage: string | null | undefined;
    isLoading: boolean;
    onRestoreTemplate: (templateId: string) => Promise<any>;
};

export function ArchivedTemplateTable(props: Props) {
    const {onRestoreTemplate} = props;
    const menuRef = React.useRef(null);

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                // @ts-expect-error
                accessor: (row) => row.name,
                dataType: 'string',
                width: 200,
            },
            {
                id: 'creatorUser',
                Header: 'Created by',
                // @ts-expect-error
                accessor: (row) => row.creatorUser,
                dataType: 'linkedEntity',
                width: 100,
            },
            {
                id: 'deletedTime',
                Header: 'Archive time',
                // @ts-expect-error
                accessor: (row) => row.deletedTime,
                dataType: 'component',
                width: 100,
                Cell: ({cell}: any) => {
                    const timestamp =
                        cell.value && cell.value > 0 ? moment.unix(cell.value) : undefined;
                    if (!timestamp) return null;

                    const TimestampComponent =
                        isToday(timestamp) || isYesterday(timestamp) ? (
                            <RelativeTimestamp datetime={cell.value} format='calendar' />
                        ) : (
                            <FormattedTimestamp
                                datetime={cell.value}
                                format={FormattedTimestamp.formats.longMonthDayWithYear}
                            />
                        );

                    return <span className='flex'>{TimestampComponent}</span>;
                },
            },
            {
                id: 'messages',
                Header: 'Messages',
                // @ts-expect-error
                accessor: (row) => row.emailSequenceTemplateMessages.length,
                dataType: 'component',
                width: 80,
                centerAlign: true,
                Cell: ({cell}: any) => {
                    const Icon = cell.value > 1 ? EmailSeriesIcon : EmailIcon;

                    return (
                        <div className='flex align-center'>
                            <Icon size={14} alignCenter={true} />
                            <div className={styles['margin-left']}>{cell.value}</div>
                        </div>
                    );
                },
            },
            {
                id: 'action',
                Header: '',
                // @ts-expect-error
                accessor: (row) => row,
                dataType: 'component',
                width: 40,
                rightAlign: true,
                disableSortBy: true,
                Cell: ({cell}: any) => {
                    if (cell.value.canEdit) {
                        const options = [
                            {
                                label: 'Restore',
                                icon: <ArchiveIcon size={16} />,
                                handleSelect: () => {
                                    onRestoreTemplate(cell.value.id);
                                },
                            },
                        ];

                        return (
                            <div className={styles['action-menu-container']}>
                                <ActionMenu
                                    getRef={(node) => {
                                        // @ts-expect-error
                                        menuRef.current = node;
                                    }}
                                    options={options}
                                    size='small'
                                />
                            </div>
                        );
                    }

                    return <></>;
                },
            },
        ],
        [onRestoreTemplate]
    );

    return (
        <LegacySetupTableContainer>
            <Table
                isResizable={false}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                headerBackgroundColor='offWhite'
                isCompact={true}
                stickyHeaders={false}
                hasBorders={true}
                hasCellBorders={false}
                isCheckboxSelectable={false}
                fullWidth={true}
                disableFilters={true}
                shouldShowFooter={false}
                // @ts-expect-error
                columns={columns}
                data={props.templates}
                sortBy={
                    props.templates.length
                        ? [
                              {
                                  id: 'name',
                                  desc: false,
                              },
                          ]
                        : undefined
                }
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.errorMessage),
                        component: <ErrorState errorMessage={props.errorMessage} />,
                    },
                    {
                        shouldRender: () => props.isLoading && !props.templates.length,
                        component: <LoadingPage padding={32} />,
                    },
                    {
                        shouldRender: () =>
                            !props.errorMessage && !props.isLoading && !props.templates.length,
                        component: <LegacySetupTableEmptyState text='No archived templates' />,
                    },
                ]}
            />
        </LegacySetupTableContainer>
    );
}
